import React, { useCallback, useEffect, useState } from "react";
import InstallerShell from "@/components/installer/InstallerShell";
import { PageContainer, PageHeader } from "@/components/layout/PageLayout";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Bell, RefreshCcw, AlertTriangle, CheckCircle2, Send } from "lucide-react";

const CHANNEL_LABEL = { in_app: "In-app", email: "Email", sms: "SMS", push: "Push" };
const STATUS_META = {
    queued:     { bg: "#F1F5F9", fg: "#475569" },
    sent:       { bg: "#DBEAFE", fg: "#1E40AF" },
    delivered:  { bg: "#DCFCE7", fg: "#166534" },
    read:       { bg: "#E5E7EB", fg: "#374151" },
    archived:   { bg: "#E5E7EB", fg: "#374151" },
    failed:     { bg: "#FEE2E2", fg: "#991B1B" },
};

/**
 * NotificationSettings — admin console.
 *
 * Presents: provider status, quiet hours, retry attempts (read-only in this
 * iteration); notification history with recipient/channel/status/error columns
 * + q search + filters; per-row and bulk retry.
 */
export default function NotificationSettings() {
    const [settings, setSettings] = useState(null);
    const [events, setEvents] = useState([]);
    const [rows, setRows] = useState([]);
    const [filters, setFilters] = useState({ q: "", channel: "", status: "", event_key: "", scope: "" });
    const [widgets, setWidgets] = useState(null);
    const [loading, setLoading] = useState(false);

    const loadHistory = useCallback(async () => {
        setLoading(true);
        try {
            const hist = await api.adminNotificationHistory(filters);
            setRows(hist);
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load history");
        } finally { setLoading(false); }
    }, [filters]);

    useEffect(() => {
        (async () => {
            try {
                const [s, ev, w] = await Promise.all([
                    api.adminNotificationSettings(),
                    api.adminNotificationEvents(),
                    api.adminNotificationWidgets(),
                ]);
                setSettings(s); setEvents(ev.events || []); setWidgets(w);
            } catch { /* ignore */ }
        })();
    }, []);

    useEffect(() => { loadHistory(); }, [loadHistory]);

    async function retryOne(id) {
        try { await api.adminNotificationRetry(id); toast.success("Retried"); await loadHistory(); }
        catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Retry failed"); }
    }
    async function retryAll() {
        try { const r = await api.adminNotificationRetryAll(); toast.success(`Retried ${r.retried}`); await loadHistory(); }
        catch { toast.error("Retry-all failed"); }
    }
    async function fireTest(event_key) {
        try {
            await api.adminTestFire({ event_key, payload: { engineer_name: "Test Engineer", customer_name: "Test Customer" } });
            toast.success(`Fired ${event_key}`);
            await loadHistory();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Test-fire failed"); }
    }

    if (!settings) {
        return (
            <InstallerShell>
                <PageContainer width="full">
                    <div className="text-sm text-[#4B5565]">Loading notification settings…</div>
                </PageContainer>
            </InstallerShell>
        );
    }

    return (
        <InstallerShell>
            <PageContainer width="full" testid="notif-admin-page">
                <PageHeader
                    title="Notification settings"
                    icon={Bell}
                    backFallback="/installer"
                    testid="notif-admin-header"
                />

                {/* Widgets */}
                {widgets && (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4" data-testid="notif-admin-widgets">
                        {/* DEV-010H post-UAT · KPI cards are now
                            actionable. Clicking a card sets a canonical
                            `scope` filter on the notification table so
                            the counts and the rows always agree. */}
                        <button type="button" onClick={() => setFilters((f) => ({ ...f, scope: f.scope === "unread_mine" ? "" : "unread_mine" }))}
                                data-testid="notif-kpi-unread"
                                aria-pressed={filters.scope === "unread_mine"}
                                className={"pe-card p-4 text-left transition-colors " + (filters.scope === "unread_mine" ? "ring-2 ring-[#131B2F]" : "hover:bg-[#F9FAFB]")}>
                            <div className="text-xs text-[#4B5565]">Unread (mine)</div>
                            <div className="text-2xl font-medium text-[#131B2F]">{widgets.unread}</div>
                        </button>
                        <button type="button" onClick={() => setFilters((f) => ({ ...f, scope: f.scope === "failed" ? "" : "failed" }))}
                                data-testid="notif-kpi-failed"
                                aria-pressed={filters.scope === "failed"}
                                className={"pe-card p-4 text-left transition-colors " + (filters.scope === "failed" ? "ring-2 ring-[#DA3535]" : "hover:bg-[#F9FAFB]")}>
                            <div className="text-xs text-[#4B5565]">Failed</div>
                            <div className="text-2xl font-medium text-[#DA3535]">{widgets.failed}</div>
                        </button>
                        <button type="button" onClick={() => setFilters((f) => ({ ...f, scope: f.scope === "pending" ? "" : "pending" }))}
                                data-testid="notif-kpi-pending"
                                aria-pressed={filters.scope === "pending"}
                                className={"pe-card p-4 text-left transition-colors " + (filters.scope === "pending" ? "ring-2 ring-[#131B2F]" : "hover:bg-[#F9FAFB]")}>
                            <div className="text-xs text-[#4B5565]">Pending</div>
                            <div className="text-2xl font-medium text-[#131B2F]">{widgets.pending}</div>
                        </button>
                    </div>
                )}

                {/* Providers */}
                <div className="pe-card p-4 mb-4" data-testid="notif-admin-providers">
                    <div className="text-sm font-medium text-[#131B2F] mb-2">Providers</div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {["email_provider", "sms_provider", "push_provider"].map((k) => {
                            const p = settings[k];
                            return (
                                <div key={k} className="border border-[#E5E7EB] rounded-lg p-3">
                                    <div className="text-xs text-[#4B5565] uppercase tracking-[0.08em]">{k.replace("_", " ")}</div>
                                    <div className="text-sm font-medium text-[#131B2F] mt-1">{p.name}</div>
                                    <div className={`text-xs mt-1 ${p.configured ? "text-[#166534]" : "text-[#92400E]"}`}>
                                        {p.configured ? "Configured" : "Stub — not yet dispatching"}
                                    </div>
                                    <div className="text-xs text-[#4B5565] mt-1">{p.note}</div>
                                </div>
                            );
                        })}
                    </div>
                    <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <div><div className="text-xs text-[#4B5565]">Sender</div>{settings.default_sender}</div>
                        <div><div className="text-xs text-[#4B5565]">Phone</div>{settings.company_phone}</div>
                        <div><div className="text-xs text-[#4B5565]">Quiet hours</div>{String(settings.quiet_hours.start).padStart(2, "0")}:00–{String(settings.quiet_hours.end).padStart(2, "0")}:00</div>
                        <div><div className="text-xs text-[#4B5565]">Retry attempts</div>{settings.retry_attempts}</div>
                    </div>
                </div>

                {/* Test fire */}
                <div className="pe-card p-4 mb-4" data-testid="notif-admin-testfire">
                    <div className="text-sm font-medium text-[#131B2F] mb-2 flex items-center gap-2">
                        <Send size={14} strokeWidth={1.75} /> Test fire an event
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {events.map((e) => (
                            <button
                                key={e.key}
                                onClick={() => fireTest(e.key)}
                                data-testid={`notif-admin-fire-${e.key}`}
                                className="px-2.5 py-1.5 rounded-md border border-[#D1D5DB] bg-white text-xs font-medium hover:bg-[#F5F5F7]"
                            >
                                {e.key}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Filters */}
                <div className="flex flex-wrap items-center gap-2 mb-3">
                    <input
                        type="text"
                        value={filters.q}
                        onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                        placeholder="Search recipient, event, subject…"
                        data-testid="notif-admin-search"
                        className="h-10 px-3 rounded-md border border-[#E5E7EB] bg-white text-sm flex-1 min-w-[220px]"
                    />
                    <select value={filters.channel} onChange={(e) => setFilters((f) => ({ ...f, channel: e.target.value }))} data-testid="notif-admin-filter-channel" className="h-10 px-2.5 rounded-md border border-[#E5E7EB] bg-white text-sm">
                        <option value="">All channels</option>
                        {Object.entries(CHANNEL_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                    <select value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))} data-testid="notif-admin-filter-status" className="h-10 px-2.5 rounded-md border border-[#E5E7EB] bg-white text-sm">
                        <option value="">Any status</option>
                        {Object.keys(STATUS_META).map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <select value={filters.event_key} onChange={(e) => setFilters((f) => ({ ...f, event_key: e.target.value }))} data-testid="notif-admin-filter-event" className="h-10 px-2.5 rounded-md border border-[#E5E7EB] bg-white text-sm">
                        <option value="">All events</option>
                        {events.map((e) => <option key={e.key} value={e.key}>{e.key}</option>)}
                    </select>
                    <button onClick={retryAll} data-testid="notif-admin-retry-all" className="h-10 px-3 rounded-md border border-[#D1D5DB] bg-white text-sm inline-flex items-center gap-1.5 hover:bg-[#F5F5F7]">
                        <RefreshCcw size={14} strokeWidth={1.75} /> Retry failed
                    </button>
                    {/* DEV-010H post-UAT · Explicit clear affordance for
                        the KPI-driven `scope` filter so operators aren't
                        stuck on a narrowed view. */}
                    {filters.scope && (
                        <button
                            type="button"
                            onClick={() => setFilters((f) => ({ ...f, scope: "" }))}
                            data-testid="notif-admin-clear-scope"
                            className="h-10 px-3 rounded-md border border-[#131B2F] bg-[#131B2F] text-white text-sm inline-flex items-center gap-1.5"
                        >
                            Showing: {filters.scope === "unread_mine" ? "Unread (mine)" : filters.scope === "failed" ? "Failed" : "Pending"} · Clear
                        </button>
                    )}
                </div>

                {/* History */}
                <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-x-auto" data-testid="notif-admin-history">
                    <table className="w-full text-sm">
                        <thead className="bg-[#F9FAFB] text-[11px] uppercase tracking-[0.04em] text-[#4B5565]">
                            <tr>
                                <th className="py-2.5 px-3 text-left">Event</th>
                                <th className="py-2.5 px-3 text-left hidden md:table-cell">Channel</th>
                                <th className="py-2.5 px-3 text-left">Recipient</th>
                                <th className="py-2.5 px-3 text-left">Status</th>
                                <th className="py-2.5 px-3 text-left hidden lg:table-cell">Sent</th>
                                <th className="py-2.5 px-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={6} className="py-6 px-3 text-center text-[#4B5565]">Loading…</td></tr>
                            ) : rows.length === 0 ? (
                                <tr><td colSpan={6} className="py-6 px-3 text-center text-[#4B5565]">No notifications match your filters</td></tr>
                            ) : rows.map((r) => {
                                const st = STATUS_META[r.status] || STATUS_META.queued;
                                return (
                                    <tr key={r.id} className="border-t border-[#F3F4F6] hover:bg-[#F9FAFB]" data-testid={`notif-admin-row-${r.id}`}>
                                        <td className="py-2 px-3">
                                            <div className="font-medium text-[#131B2F]">{r.event_key}</div>
                                            <div className="text-xs text-[#4B5565] truncate max-w-[280px]">{r.summary || r.subject}</div>
                                        </td>
                                        <td className="py-2 px-3 hidden md:table-cell text-[#4B5565]">{CHANNEL_LABEL[r.channel] || r.channel}</td>
                                        <td className="py-2 px-3">
                                            <div className="text-[#131B2F]">{r.recipient_name || "—"}</div>
                                            <div className="text-xs text-[#4B5565] truncate max-w-[220px]">{r.recipient_address || ""}</div>
                                        </td>
                                        <td className="py-2 px-3">
                                            <span className="inline-block px-2 py-0.5 rounded-full text-[11px] font-medium" style={{ background: st.bg, color: st.fg }}>
                                                {r.status}
                                            </span>
                                            {r.error && <div className="text-[11px] text-[#DA3535] mt-1 truncate max-w-[220px]" title={r.error}>{r.error}</div>}
                                        </td>
                                        <td className="py-2 px-3 hidden lg:table-cell text-xs text-[#4B5565]">
                                            {r.sent_at ? new Date(r.sent_at).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
                                        </td>
                                        <td className="py-2 px-3 text-right">
                                            {r.status === "failed" && (
                                                <button onClick={() => retryOne(r.id)} data-testid={`notif-admin-retry-${r.id}`} className="text-sm text-[#3E9E2E] hover:underline">Retry</button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <div className="mt-3 text-xs text-[#4B5565]" data-testid="notif-admin-count">{rows.length} notifications</div>
            </PageContainer>
        </InstallerShell>
    );
}
